import { FC, useEffect } from "react";
import { observer } from "mobx-react";
import { PasswordResetModel, usePasswordResetModel } from "./loginDomain";
import { Box, Card, Container, VBox } from "../components-v2/Box";
import { LoadingIndicator } from "../components-v2/LoadingIndicator";
import { Alert } from "../components-v2/Alert";
import { Button, LinkButton } from "../components-v2/Button";
import { navigateToLogin } from "../utils/navigation";
import Form from "../components-v2/Form";
import { MTextInput } from "../components-v2/TextInput";
import { LoginHeader } from "./loginComponents";
import { useRootStore, useTranslation } from "../stores/RootStoreContext";
import { QueryParams } from "../utils/queryParams";

const PasswordResetPage: FC<{ queryParams: QueryParams }> = observer(({ queryParams }) => {
  const token = queryParams.ref ?? null;
  const model = usePasswordResetModel({ token });
  return <PasswordReset model={model} />;
});

export const PasswordReset = observer(function PasswordReset({
  model,
}: {
  model: PasswordResetModel;
}) {
  const { t } = useTranslation();

  useEffect(() => {
    model.checkToken();
  }, [model]);

  const history = useRootStore().history;
  return (
    <Container maxWidth={"sm"}>
      <LoginHeader />
      <Card padding="lg">
        {model.state === "reference-check" ? (
          <LoadingIndicator />
        ) : model.state === "invalid-token" ? (
          <VBox>
            <Alert message={t("forgotpassword.invalid-token")} type={"error"} />
            <LinkButton label={t("back")} arrow={"left"} onClick={() => navigateToLogin(history)} />
          </VBox>
        ) : model.state === "success" ? (
          <VBox>
            <Alert message={t("forgotpassword.success")} type={"success"} />
            <Box>
              <LinkButton
                label={t("forgotpassword.back-to-login")}
                arrow={"left"}
                onClick={() => navigateToLogin(history)}
              />
            </Box>
          </VBox>
        ) : (
          <Form submitOnEnter onSubmit={() => model.requestNewPassword()}>
            <VBox>
              {model.error && <Alert message={t(model.error)} type={"error"} />}
              <h5>{t("forgotpassword.new-password")}</h5>
              <MTextInput
                model={model.passwordModel}
                autofocus
                type="password"
                disabled={model.submitting}
              />
              <h5>{t("forgotpassword.repeat-password")}</h5>
              <MTextInput
                model={model.repeatPasswordModel}
                type="password"
                disabled={model.submitting}
              />
              <Button
                variant={"primary"}
                label={t("forgotpassword.create-new-password")}
                type={"submit"}
                loading={model.submitting}
                disabled={model.submitting}
              />
            </VBox>
          </Form>
        )}
      </Card>
    </Container>
  );
});

export default PasswordResetPage;
